import React, { useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { setAccessToken, setUserData } from "./utils/auth";
import { CheckCircle, AlertCircle, Loader } from "lucide-react";
import { useTranslation } from "./contexts/LocaleContext";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const { lang } = useParams();
  const navigate = useNavigate();
  const t = useTranslation();

  // Helper to get localized link
  const getLocalizedLink = (path: string) => {
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    if (lang && lang !== "cs") {
      return `/${lang}/${cleanPath}`;
    }
    return `/${cleanPath}`;
  };

  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setStatus("error");
      setMessage(t.landing.resetPassword.passwordsDoNotMatch);
      return;
    }

    if (!token) {
      setStatus("error");
      setMessage(t.landing.resetPassword.invalidLink);
      return;
    }

    setStatus("loading");
    try {
      const response = await fetch("/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus("success");
        setMessage(data.message || t.landing.resetPassword.success);

        // Auto-login if token is provided
        if (data.accessToken) {
          setAccessToken(data.accessToken);
          if (data.user) setUserData(data.user);
          setTimeout(() => {
            navigate(getLocalizedLink("/invoice-list"));
          }, 2000);
        } else {
          setTimeout(() => {
            navigate(getLocalizedLink("/login"));
          }, 3000);
        }
      } else {
        setStatus("error");
        // Map backend error code to localized string
        const errorCode = data.error as keyof typeof t.landing.auth.errors;
        setMessage(
          t.landing.auth.errors[errorCode] ||
            data.error ||
            t.landing.resetPassword.invalidLink
        );
      }
    } catch (err) {
      setStatus("error");
      setMessage(t.landing.auth.errors.FAILED_TO_RESET);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-950 via-slate-950 to-black px-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 text-center">
          <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
            {t.landing.resetPassword.invalidLink}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            {t.landing.resetPassword.invalidLinkDescription}
          </p>
          <button
            onClick={() => navigate(getLocalizedLink("/login"))}
            className="w-full px-4 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm transition-all shadow-sm border-0"
          >
            {t.landing.resetPassword.backToLogin}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-950 via-slate-950 to-black px-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8">
        {status === "success" ? (
          <div className="text-center">
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {t.landing.resetPassword.success}
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
            <p className="text-sm text-slate-500">
              {t.landing.resetPassword.redirecting}
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6 text-center">
              {t.landing.resetPassword.title}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="new-password"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >
                  {t.landing.resetPassword.newPassword}
                </label>
                <input
                  id="new-password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-slate-900 dark:text-white text-sm"
                  placeholder={t.landing.resetPassword.newPassword}
                />
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                >
                  {t.landing.resetPassword.confirmPassword}
                </label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-slate-900 dark:text-white text-sm"
                  placeholder={t.landing.resetPassword.confirmPassword}
                />
              </div>

              {status === "error" && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full px-4 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm transition-all shadow-sm border-0 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status === "loading" ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    {t.landing.resetPassword.resetting}
                  </>
                ) : (
                  t.landing.resetPassword.saveButton
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

